import { Alert, DropdownProps } from "@fluentui/react-northstar";
import { FieldDescriptor } from "@shopify/react-form-state";
import React, { useMemo } from "react";

import { Dropdown } from "./../Dropdown";
import { FieldLabel, FieldLabelProps } from ".";

type DropdownItemValueType = string | number | boolean | undefined;

type Props<T extends DropdownItemValueType> = {
  label?: string;
  field: FieldDescriptor<T>;
  labelProps?: Partial<FieldLabelProps>;
  items: Array<DropdownItem<T>>;
  style?: React.CSSProperties;
  tooltip?: React.ReactNode;
  onChanged?: (value: T) => boolean;
  className?: string;
  selectedItemLabel?: (item: DropdownItem<T>) => React.ReactNode;
  itemRender?: (value: DropdownItem<T>) => React.ReactNode;
  itemSelectedRender?: (item: DropdownItem<T>) => React.ReactNode;
  isMobile?: boolean;
} & DropdownProps;

export type DropdownItem<T extends DropdownItemValueType> = { label: React.ReactNode; value: T };

export function DropdownField<T extends DropdownItemValueType>(props: Props<T>): JSX.Element {
  const {
    label,
    field,
    items,
    style,
    tooltip,
    className,
    defaultValue,
    labelProps,
    isMobile,
    onChanged,
    itemRender,
    itemSelectedRender,
    selectedItemLabel,
    disabled,
    ...dropdownProps
  } = props;

  const selectedItem = useMemo(() => {
    let selected = items.find(f => f.value === field.value);
    if (!selected) {
      selected = { label: "", value: field.value };
    }
    if (selectedItemLabel) {
      selected.label = selectedItemLabel(selected);
    }
    return selected;
  }, [items, field.value, selectedItemLabel]);

  return (
    <div style={style} className={className + " dropdown-field"}>
      {label && <FieldLabel label={label} tooltip={tooltip} size="medium" {...labelProps} />}

      <div style={label ? { marginTop: ".25rem" } : {}}>
        <Dropdown
          {...(isMobile && { design: { paddingLeft: "5px;" }, inline: true })}
          items={items}
          disabled={disabled}
          className={disabled ? "disabled" : ""}
          isItemSelected={item => {
            const val = item.value?.toString();

            return val === field.value;
          }}
          value={selectedItem.label}
          renderSelectedItem={(Item, props) => {
            const header = itemSelectedRender
              ? itemSelectedRender(props as DropdownItem<T>)
              : (props as DropdownItem<T>).label;

            return <Item {...props} header={header} />;
          }}
          renderItem={(Item, props) => (
            <Item
              {...props}
              key={(props as DropdownItem<T>).value?.toString()}
              header={
                itemRender ? itemRender(props as DropdownItem<T>) : (props as DropdownItem<T>).label
              }
            />
          )}
          onChange={(_e, d) => {
            if (d) {
              const item = d.value as DropdownItem<T>;

              if (!onChanged || !onChanged(item.value)) {
                field.onChange(item.value);
              }
            }
          }}
          {...dropdownProps}
        />
      </div>

      {field.error && <Alert content={field.error} danger attached="bottom" />}
    </div>
  );
}
